import type { IntakeFormData } from "./form";

/**
 * The one canonical stored value for a commonly-controlled multi-employer plan.
 * Used as a picklist value, in validation, and in the unsupported-plan hard stop.
 */
export const COMMONLY_CONTROLLED = "Commonly controlled group of employers per IRS";

/**
 * Sentinel entityId used on OwnershipAssignment rows that belong to the primary
 * group (Entity 1 — the employer completing the intake). Affiliated entity
 * assignments carry the AffiliatedGroup.id instead. The primary group has no
 * client-side record of its own, so a stable constant keeps the assignment
 * shape uniform in the store, in validation, and in the render loop.
 */
export const PRIMARY_ENTITY_ID = "primary";

/**
 * Display labels for Step 3 (Company Structure) picklist values.
 * Stored values are the long verbatim Salesforce strings; the Structure dropdown
 * shows abbreviated labels, and the Review / Thank You summaries should match.
 * Values not in the map fall through to themselves at the call site.
 */
export const MULTI_EMPLOYER_LABELS: Record<string, string> = {
  [COMMONLY_CONTROLLED]: "Commonly controlled (IRS)",
  "Professional Employer Organization (PEO)": "PEO",
  "Multiple Employer Welfare Arrangement (MEWA)": "MEWA",
};

/**
 * Hard-stop copy shown inline on the Structure page and in the submit-blocking toast.
 * Optimyl does not underwrite multi-employer plans unless they are a common control group.
 */
export const MULTI_EMPLOYER_UNSUPPORTED_MSG =
  "Optimyl does not underwrite plans that cover employees of more than one employer unless part of a common control group.";

/**
 * A multi-employer plan is a hard stop whenever it covers more than one employer and the
 * selected classification is anything other than a common control group (PEO, MEWA, Church,
 * Employer Association, Taft Hartley Union). The classification itself is irrelevant — all
 * non-common-control types are unsupported.
 */
export const isUnsupportedMultiEmployer = (d: Pick<IntakeFormData, "isMultiEmployer" | "multiEmployerType">): boolean =>
  d.isMultiEmployer === "Yes" && d.multiEmployerType !== "" && d.multiEmployerType !== COMMONLY_CONTROLLED;

/**
 * True whenever the intake surfaces the affiliated-entity list on Step 3:
 *  - Multi-employer=Yes + Commonly Controlled, OR
 *  - Multi-employer=No + Commonly-Controlled-Group=Yes.
 *
 * Same predicate drives the primary-Entity-1 card render, the affiliated-groups
 * seeding effect, the intakeApi's decision to compose a primary entry into
 * Affiliation_Data__c on save, and the Review/Thank You page's per-entity
 * layout — one shared definition so the paths can't drift.
 */
export const isAffiliatedListShown = (
  d: Pick<IntakeFormData, "isMultiEmployer" | "multiEmployerType" | "isCommonlyControlledGroup">,
): boolean =>
  (d.isMultiEmployer === "Yes" && d.multiEmployerType === COMMONLY_CONTROLLED)
  || (d.isMultiEmployer === "No" && d.isCommonlyControlledGroup === "Yes");

/** Hard cap on the Company Owners roster. The Step 3 UI disables the "Add
 *  Company Owner" button once this many are entered, and the store's
 *  `addOwner` action drops any attempt to add past the cap as a defense.
 *  Per-entity Ownership Breakdown rows are transitively capped at the same
 *  ceiling because each row must reference a distinct roster owner. */
export const MAX_OWNERS = 250;

/** Hard cap on the number of affiliated (additional) entities beyond the
 *  primary group. The Step 3 UI hides the "Add Another Entity" button once
 *  this many affiliated groups have been added. */
export const MAX_AFFILIATED_ENTITIES = 20;

/** The PEO situation value that is a hard stop — a PEO master plan. */
export const PEO_MASTER_PLAN = "master-plan";

/** Hard-stop copy for PEO master plans, shown in the submit-blocking toast (BottomNav). */
export const PEO_UNSUPPORTED_MSG = "Optimyl Benefits does not underwrite PEO master plans.";

/** A PEO master plan is a hard stop — Optimyl does not underwrite it. */
export const isUnsupportedPeoMasterPlan = (d: Pick<IntakeFormData, "hasPeo" | "peoType">): boolean =>
  d.hasPeo === "Yes" && d.peoType === PEO_MASTER_PLAN;

/**
 * Readable expansions for the `peoType` stored codes. Both the Review and Thank You
 * summaries use this to render "Yes — our group is a PEO master plan" etc. instead of
 * the raw kebab-case value (or, worse, a bare "Yes" that drops the follow-up detail).
 * Values not in the map fall through to plain "Yes" at the call site.
 */
export const PEO_LABELS: Record<string, string> = {
  [PEO_MASTER_PLAN]: "Yes — our group is a PEO master plan",
  "leaving": "Yes — but we are leaving the PEO",
  "no-medical": "Yes — but we do not receive medical benefits through the PEO",
};

/**
 * Nonprofit/Other organizations are exempt from the ownership REQUIREMENT
 * (Mike Bentz, 2026-08-12): no minimum owner roster and no per-entity 100%
 * totals. Rows the user does enter are still validated for completeness.
 * Shared by the Step 3 schema and StepStructure's per-entity error rendering
 * so the two can never disagree.
 */
export const isOwnershipExempt = (d: Pick<IntakeFormData, "orgType">): boolean =>
  d.orgType === "Nonprofit" || d.orgType === "Other";
